import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Card, Empty, Skeleton, Space, Typography, message } from 'antd'
import { Link } from 'react-router-dom'
import { PlusOutlined } from '@ant-design/icons'
import B2BLayout from '../../../layouts/b2b'
import fetchAPI, { IHttpError } from '../../../../lib/utils/fetch-api'
import Result, { ResultStatusType } from 'antd/lib/result'
import { ImportOrdersBlock } from '../../../components/v2/import-orders-block'
import type { CompanyStructure } from '../../home/company/types'
import { flattenOfficeOptionsForOrders, hasDealerOffices } from '../../home/import-utils'

const { Title, Text } = Typography

interface IError {
  code?: ResultStatusType
  message?: string
}

const OrdersImport = () => {
  const [loading, setLoading] = useState<boolean>(true)
  const [accessError, setAccessError] = useState<IError | null>(null)
  const [structure, setStructure] = useState<CompanyStructure | null>(null)

  const breadcrumbs = useMemo(
    () => [
      { href: '/admin', title: 'Админ панель' },
      { href: '/admin/orders_import', title: 'Импорт заказов' },
    ],
    [],
  )

  const orderOfficeOptions = useMemo(
    () => (structure ? flattenOfficeOptionsForOrders(structure) : []),
    [structure],
  )

  const checkAccess = useCallback(async () => {
    setLoading(true)
    try {
      const user_permissions = await fetchAPI('/api/user_permissions')
      const hasPermission = user_permissions.includes('admin_orders_import')
      if (!hasPermission) {
        setAccessError({
          message: 'У вас нет доступа к этому разделу.',
          code: 403,
        })
        return
      }
      setAccessError(null)
      const res = await fetchAPI('/api/home/company-structure')
      if (res?.errors) {
        message.error(res.errors?.[0]?.detail || 'Не удалось загрузить офисы')
        return
      }
      setStructure(res?.data || null)
    } catch (e) {
      setAccessError({
        message: (e as IHttpError)?.message || 'Неизвестная ошибка',
        code: ((e as IHttpError)?.statusCode || 'error') as ResultStatusType,
      })
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    checkAccess()
  }, [checkAccess])

  const dealerOfficesAvailable = Boolean(structure && hasDealerOffices(structure))

  return (
    <B2BLayout breadcrumbs={breadcrumbs}>
      {loading && <Skeleton active />}
      {!loading && accessError && (
        <Result status={accessError.code} title="Ошибка доступа" subTitle={accessError.message} />
      )}
      {!loading && !accessError && (
        <Card>
          <Title level={2} style={{ marginTop: 0 }}>
            Импорт заказов
          </Title>
          {!dealerOfficesAvailable ? (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Space direction="vertical" size={4}>
                  <Text>Импорт заказов доступен только для офисов организаций типа «Дилер».</Text>
                  <Text type="secondary">Создайте дилерскую организацию и офис.</Text>
                </Space>
              }
            >
              <Link to="/new/organizations">
                <Button type="primary" icon={<PlusOutlined />}>
                  К организациям
                </Button>
              </Link>
            </Empty>
          ) : (
            <ImportOrdersBlock variant="inline" officeOptions={orderOfficeOptions} />
          )}
        </Card>
      )}
    </B2BLayout>
  )
}

export default memo(OrdersImport)
